// ================================
// SERVICE VENTURES - CENTRALIZED DATA FILE
// ================================
// Single source of truth for all application data
// Last Updated: November 2025


// ================================
// CONFIGURATION & CONSTANTS
// ================================
export const config = {
  colors: {
    navyBlue: '#080b35',
    blue: '#333790', 
    lightBlue: '#77a7f7',
    white: '#FFFFFF'
  },
  validPasswords: ['board2025', 'serviceventures', 'q4meeting'],
  companyInfo: {
    name: 'Service Ventures LLC',
    parentCompany: 'Service Federal Credit Union (NH)',
    assets: '$6B',
    members: '375K',
    fundCapacity: '$60M'
  }
};

// ================================
// AGENDA ITEMS
// ================================
export const agendaItems = [
  { id: 'dashboard', title: 'Home', duration: '5 min', icon: 'BarChart3', visible: true },
  { id: 'financial', title: 'Performance', duration: '15 min', icon: 'DollarSign', visible: true },
  { id: 'portfolio', title: 'Portfolio', duration: '20 min', icon: 'Building', visible: true },
  { id: 'pipeline', title: 'Pipeline', duration: '15 min', icon: 'TrendingUp', visible: true },
  { id: 'decisions', title: 'Board Decisions', duration: '10 min', icon: 'CheckCircle', visible: false },
  { id: 'supporting', title: 'Supporting Materials', duration: '5 min', icon: 'FileText', visible: false }
];

// ================================
// BOARD MEETING REPORTS
// ================================
export const boardMeetingReports = [
  {
    id: 'september-2025',
    date: '2025-09-15',
    title: 'September 2025',
    quarter: 'Q3',
    year: 2025,
    type: 'monthly',

    // Financial snapshot at this point in time (September YTD values)
    financialSnapshot: {
      equity: 0,
      efficiency: 1440615,
      income: 114517,
      contract: 174925,
      total: 1730057
    },

    // Monthly returns (just for this month)
    monthlyReturns: {
      equity: 0,
      efficiency: 144566,
      income: 5279,
      contract: 44053,
      total: 193898
    },

    // Quarterly total (Q3: Jul + Aug + Sep)
    quarterlyTotal: 551805,

    // Waterfall data for charts
    waterfallData: {
      // Month-over-Month comparison (August → September)
      monthOverMonth: {
        previousMonth: 'August',
        previousMonthTotal: 196908,
        changes: {
          equity: 0,
          efficiency: 1783,  // 144,566 - 151,983
          income: -15807,     // 5,279 - 21,085
          contract: 11013,    // 44,053 - 33,040
          total: -3011       // Change from Aug to Sep
        },
        currentMonthTotal: 193898
      },

      // Quarter-to-Date (Q2 → Q3)
      quarterToDate: {
        previousQuarter: 'Q2',
        previousQuarterTotal: 533508,
        changes: {
          equity: 0,
          efficiency: -50160,  // Q3 efficiency - Q2 efficiency
          income: 6333,       // Q3 income - Q2 income
          contract: 62074,     // Q3 contract - Q2 contract
          total: 18247         // Q3 total - Q2 total
        },
        currentQuarterTotal: 551805
      },

      // Year-to-Date cumulative
      yearToDate: {
        startingValue: 0,
        changes: {
          equity: 0,
          efficiency: 1440615,
          income: 114517,
          contract: 174925,
          total: 1730057
        },
        endingValue: 1730057
      }
    },

    executiveSummary: {
      quote: "YTD Total returns through September are $1.730M, $1.4M from Operational Efficiency, $0.1M from Income, $0.2M from Contract savings. We continue to see strong performance across all categories, driven by Confer, Saris AI, and Posh. ScribeUp surpassed 18,500 active members this month. Forecast for the full year remains on track at $2.4M in total return.",
      author: 'Brian, General Partner'
    },

    keyResults: [
      { text: 'ScribeUp surpassed 18,500 active members with strong retention and engagement', color: 'green' },
      { text: 'Posh chat bot expansion showing increased engagement across member channels', color: 'blue' },
      { text: 'Confer delivering consistent efficiency returns with credit union partnerships', color: 'purple' },
      { text: 'Saris AI preparing for mortgage team pilot program launch in Q4', color: 'green' }
    ],

    strategicProgress: [
      { text: 'Built comprehensive investment tracking metrics framework across portfolio', color: 'indigo' },
      { text: 'Advanced ModernFi vendor onboarding process for Q4 deposit intake launch', color: 'teal' },
      { text: 'Initiated data analytics partnership for enhanced portfolio reporting capabilities', color: 'rose' }
    ],

    topPerformers: [
      { companyId: 'confer', metric: 'YTD Efficiency', value: '$1.1M' },
      { companyId: 'scribeup', metric: 'YTD Returns', value: '$168K' }
    ],

    strategicInitiatives: null,

    // Customizable topics for this reporting period
    otherTopics: [
      {
        title: 'Q3 Portfolio Performance',
        items: [
          'Confer maintaining strong efficiency returns with existing credit union partnerships',
          'ScribeUp member growth accelerating with improved engagement and retention metrics',
          'Posh expanding chat capabilities to drive additional value beyond voice automation'
        ]
      },
      {
        title: 'Q4 Pipeline & Preparation',
        items: [
          'ModernFi vendor onboarding progressing on schedule for deposit intake by year end',
          'WealthCabinet integration planning aligned with Alkami migration timeline',
          'Saris AI pilot program scoped with mortgages team for November launch'
        ]
      },
      {
        title: 'Investment Framework & Analytics',
        items: [
          'Developed comprehensive metrics dashboard for real-time portfolio performance tracking',
          'Established data analytics collaboration to enhance investment reporting capabilities',
          'Standardized quarterly business review process across all active portfolio companies'
        ]
      }
    ]
  },
  {
    id: 'october-2025',
    date: '2025-10-15',
    title: 'October 2025',
    quarter: 'Q4',
    year: 2025,
    type: 'monthly',

    // Financial snapshot at this point in time (current YTD values)
    financialSnapshot: {
      equity: 0,
      efficiency: 1634219,
      income: 124747,
      contract: 216318,
      total: 1975284
    },

    // Monthly returns (just for this month)
    monthlyReturns: {
      equity: 0,
      efficiency: 193604,
      income: 10230,
      contract: 41393,
      total: 245227
    },

    // Quarterly total (Q4: just Oct so far)
    quarterlyTotal: 245227,

    // Waterfall data for charts (from CSV data)
    waterfallData: {
      // Month-over-Month comparison (September → October)
      monthOverMonth: {
        previousMonth: 'September',
        previousMonthTotal: 193897,
        changes: {
          equity: 0,
          efficiency: 49038,   // From CSV: "Efficiency, $49,038"
          income: 4951,        // From CSV: "Income, $4,951"
          contract: -2660,     // From CSV: "Contract, $(2,660)"
          total: 51329         // 245,227 - 193,897 (note: CSV shows $51,330 due to rounding)
        },
        currentMonthTotal: 245227
      },

      // Quarter-to-Date (Q3 → Q4 QTD)
      quarterToDate: {
        previousQuarter: 'Q3',
        previousQuarterTotal: 551805,
        changes: {
          equity: 0,
          efficiency: -205377,  // From CSV: "Efficiency, $(205,377)"
          income: -33788,       // From CSV: "Income, $(33,788)"
          contract: -67412,     // From CSV: "Contract, $(67,412)"
          total: -306578        // 245,227 - 551,805
        },
        currentQuarterTotal: 245227
      },

      // Year-to-Date cumulative (from CSV "2025 Year to Date")
      yearToDate: {
        startingValue: 0,      // From CSV: "2024, $-"
        changes: {
          equity: 0,
          efficiency: 1634219,  // From CSV: "Efficiency, $1,634,219"
          income: 124747,       // From CSV: "Income, $124,747"
          contract: 216318,     // From CSV: "Contract, $216,318"
          total: 1975284
        },
        endingValue: 1975284   // From CSV: "October, $1,975,284"
      }
    },

    executiveSummary: {
      quote: "YTD Total returns are $1.975M, $1.6M from Operational Efficiency, $0.1M from Income, $0.2M from Contract savings. We continue to see increases in all categories, mostly driven by Confer, Saris AI, and Posh. This month we began vendor onboarding for ModernFi and WealthCabinet is being offered to our members with the Alkami migration. Forecast for the full year remains at $2.4M in total return.",
      author: 'Brian, General Partner'
    },

    keyResults: [
      { text: 'Began onboarding ModernFi as a vendor - planning to take deposits by end of 2025', color: 'green' },
      { text: 'WealthCabinet went live with Alkami migration, now available to members', color: 'blue' },
      { text: 'Posh chat offered "behind the pin" - expecting large increase in chat volume', color: 'purple' },
      { text: 'Saris AI to begin testing phase with mortgages team in November', color: 'green' }
    ],

    strategicProgress: [
      { text: 'Built out full investment metrics across the portfolio for better tracking and reporting', color: 'indigo' },
      {
        segments: [
          'Partnered with data analytics on ',
          { text: 'direct deposit analysis', link: '/analysis/direct-deposit-analysis' },
          ' to support Silvur investment business case'
        ],
        color: 'teal'
      },
      { text: 'Analysis on Qnext (FileFlex) investment - opportunity to increase NPS and reduce security risk', color: 'rose' }
    ],

    topPerformers: [
      { companyId: 'confer', metric: 'YTD Efficiency', value: '$1.2M' },
      { companyId: 'scribeup', metric: 'YTD Returns', value: '$200K' }
    ],

    strategicInitiatives: null,

    // Customizable topics for this reporting period
    otherTopics: [
      {
        title: 'Confer Lending Updates',
        items: [
          'Confer recently signed deal with Navigant Credit Union, working on deal with Jeanne D\'arc Credit Union. As a reminder, Service Venutres has a contractual right to double our equity if Confer signs 8 new CUs or $2M ARR by end of 2026',
          'Service CU has seen a significant reduction in the funding rate for Consumer loans. Working with Heather and Confer to auto-fund CCs, Personal loans. Implementing tracking for auto loan days in queue.',
          'Auto-funding will drive significant increase in operational efficiency. More loans funded + relieve back office operations to focus on closing auto loans.'
        ]
      },
      {
        title: 'Data Analytics Progress',
        items: [
          'Using Snowflake to analyze transaction, member, and account data for portfolio metrics',
          'Partnering with Data Analytics team to create final versions of calculations',
          'Goal: Build real-time dashboard in Snowflake for board updates on operational metrics'
        ]
      },
      {
        title: 'Portfolio Milestones',
        items: [
          'ModernFi: Planning deposit intake by end of 2025, network deposits in H1 2026',
          'WealthCabinet: Successfully launched with Alkami migration',
          'Posh: Enhanced chat functionality deployment expected to drive significant engagement'
        ]
      }
    ]
  },
  {
    id: 'november-2025',
    date: '2025-12-04',
    title: 'November 2025',
    quarter: 'Q4',
    year: 2025,
    type: 'monthly',

    // Financial snapshot at this point in time (November YTD values)
    financialSnapshot: {
      equity: 0,
      efficiency: 1828306,
      income: 135871,
      contract: 258517,
      total: 2222694
    },

    // Monthly returns (just for this month)
    monthlyReturns: {
      equity: 0,
      efficiency: 194088,
      income: 11123,
      contract: 42200,
      total: 247411
    },

    // Quarterly total (Q4: Oct + Nov)
    quarterlyTotal: 492638,

    // Per-company breakdown data for charts
    companyBreakdown: {
      // Month-to-Date (November 2025)
      mtd: [
        { company: 'Confer', code: 'confer', efficiency: 125000, income: 3123, contract: 0, total: 128123 },
        { company: 'CUautoFi', code: 'cuautofi', efficiency: 36369, income: 0, contract: 0, total: 36369 },
        { company: 'ModernFi', code: 'modernfi', efficiency: 0, income: 0, contract: 6000, total: 6000 },
        { company: 'Posh', code: 'posh', efficiency: 16160, income: 0, contract: 4417, total: 20577 },
        { company: 'Saris AI', code: 'sarisai', efficiency: 0, income: 0, contract: 22917, total: 22917 },
        { company: 'ScribeUp', code: 'scribeup', efficiency: 16559, income: 8000, contract: 8866, total: 33425 }
      ],
      // Quarter-to-Date (Q4 2025: Oct + Nov)
      qtd: [
        { company: 'Confer', code: 'confer', efficiency: 250000, income: 5963, contract: 0, total: 255963 },
        { company: 'CUautoFi', code: 'cuautofi', efficiency: 72738, income: 0, contract: 0, total: 72738 },
        { company: 'ModernFi', code: 'modernfi', efficiency: 0, income: 0, contract: 12000, total: 12000 },
        { company: 'Posh', code: 'posh', efficiency: 32160, income: 0, contract: 8833, total: 40993 },
        { company: 'Saris AI', code: 'sarisai', efficiency: 0, income: 0, contract: 45833, total: 45833 },
        { company: 'ScribeUp', code: 'scribeup', efficiency: 32794, income: 15391, contract: 16926, total: 65111 }
      ],
      // Year-to-Date (2025)
      ytd: [
        { company: 'Confer', code: 'confer', efficiency: 1441324, income: 27155, contract: 0, total: 1468479, percentOfTotal: 66 },
        { company: 'CUautoFi', code: 'cuautofi', efficiency: 168637, income: 0, contract: 0, total: 168637, percentOfTotal: 8 },
        { company: 'ModernFi', code: 'modernfi', efficiency: 0, income: 0, contract: 22000, total: 22000, percentOfTotal: 1 },
        { company: 'Posh', code: 'posh', efficiency: 111780, income: 0, contract: 48583, total: 160363, percentOfTotal: 7 },
        { company: 'Saris AI', code: 'sarisai', efficiency: 0, income: 0, contract: 137500, total: 137500, percentOfTotal: 6 },
        { company: 'ScribeUp', code: 'scribeup', efficiency: 106565, income: 108716, contract: 50434, total: 265715, percentOfTotal: 12 }
      ]
    },

    // Financial commentary by period
    financialCommentary: {
      mtd: {
        summary: 'Total efficiency for November 2025 is $247K, driven primarily by operational efficiency ($194K), supported by contractual efficiency ($42K) and income ($11K).',
        efficiency: [
          'Confer continues to drive the majority (64%) of our operational efficiency, with an estimated $125K of operational efficiency per month based on the current campaign trends. This will be adjusted as the campaign continues and can be heavily impacted by loan funding rates.',
          'CUautoFi created $36K of efficiency due to headcount reduction leading up to the CUautoFi conversion.',
          'Posh created $16K by deflecting chats & calls.',
          'ScribeUp created $16K with recurring transaction retention.'
        ],
        contract: [
          'Saris AI 50% contract reduction drives $23K per month.',
          'ScribeUp 50% contract reduction drives $9K per month at current member volume.',
          'ModernFi contract reduction drives $6K per month. This contract reduction is not a set %, it\'s an entirely new pricing model that removes platform minimums.',
          'Posh 25% contract reduction drives $4K per month.'
        ],
        income: [
          'Increased card usage by ScribeUp users is estimated to drive $8K in additional net non-interest income in November.',
          'Confer generated credit card accounts are estimated to drive $3K in net non-interest income in November.'
        ]
      },
      qtd: {
        summary: 'Fourth quarter is projected to have $740K in total returns, an increase of $189K or 34% over the third quarter\'s result of $551K.',
        changes: [
          '$141K from efficiency: Confer Lending is projected to have significantly more loan volume with the conversion to Alkami, and funding rates are expected to improve over the significant drop we saw in Q3.',
          '$25K from contracts: Q4 is the first full quarter of results from CUautoFi and ModernFi, as well as expected adoption increases in ScribeUp.',
          'The remaining increase is a forecast of income increase across credit card products.'
        ],
        variables: [
          'Confer lending results are driven based on funded loans. Loan pull-through rates are critical to determining the final results for Confer.',
          'ScribeUp adoption and member spending habits will impact efficiency and income statistics.',
          'Posh usage with Alkami updates is unknown, but we planned for increased call volume and chat deflection based on the online banking conversion and chat being \'behind the pin\'.'
        ]
      },
      ytd: {
        summary: 'Through November 2025, Service Ventures generated $2.2M of total return and is estimated to return $2.4M by the end of 2025.',
        drivers: [
          'Confer drives $1.5M or 68% of returns. The overwhelming majority of this is through their pre-approval lending product ($1.4M), with the remaining return being generated based on the credit card usage from Confer generated loans.',
          'ScribeUp generates $250K or 11% of total return through contract efficiency, increased member relationships (transaction activity and retention).',
          'Posh generates $160K or 7% of returns by deflecting call and chat volume from the call center employees.'
        ]
      }
    },

    // Waterfall data for charts
    waterfallData: {
      // Month-over-Month comparison (October → November)
      monthOverMonth: {
        previousMonth: 'October',
        previousMonthTotal: 245227,
        changes: {
          equity: 0,
          efficiency: 485,
          income: 893,
          contract: 806,
          total: 2184
        },
        currentMonthTotal: 247411
      },

      // Quarter-to-Date (Q3 → Q4 QTD)
      quarterToDate: {
        previousQuarter: 'Q3',
        previousQuarterTotal: 551805,
        changes: {
          equity: 0,
          efficiency: -11289,
          income: -22665,
          contract: -25213,
          total: -59167
        },
        currentQuarterTotal: 492638
      },

      // Year-to-Date cumulative
      yearToDate: {
        startingValue: 0,
        changes: {
          equity: 0,
          efficiency: 1828306,
          income: 135871,
          contract: 258517,
          total: 2222694
        },
        endingValue: 2222694
      }
    },

    executiveSummary: {
      quote: "Financial results for 2025 are projected to be $2.4M but depend heavily on funding rates for Confer, which have varied significantly over the last two quarters. Progress continues integrating with our investment partners to drive the maximum value for Service CU, and there are healthy deals in the pipeline with Silvur and FileFlex that can drive significant operational efficiency.",
      author: 'Brian, General Partner'
    },

    keyResults: [
      { text: 'November total returns of $247K driven primarily by operational efficiency ($194K)', color: 'green' },
      { text: 'Confer generated $128K in November (52% of monthly returns) through pre-approval lending efficiency and credit card income', color: 'blue' },
      { text: 'CUautoFi created $36K efficiency from headcount reduction leading up to conversion', color: 'purple' },
      { text: 'Contract savings of $42K driven by Saris AI ($23K), ScribeUp ($9K), ModernFi ($6K), and Posh ($4K)', color: 'green' }
    ],

    strategicProgress: [
      {
        segments: [
          'Investment pipeline active with ',
          { text: 'Silvur', link: '/pipeline' },
          ', ',
          { text: 'FileFlex', link: '/pipeline' },
          ', and ',
          { text: 'VisionFi', link: '/pipeline' },
          ' in various stages of diligence'
        ],
        color: 'indigo'
      },
      { text: 'ModernFi deposit network integration targeting test in January 2026', color: 'teal' },
      { text: 'Saris AI development with mortgages team targeting testing phase before year end', color: 'rose' }
    ],

    topPerformers: [
      { companyId: 'confer', metric: 'YTD Efficiency', value: '$1.4M' },
      { companyId: 'scribeup', metric: 'YTD Returns', value: '$266K' }
    ],

    strategicInitiatives: null,

    // Portfolio Operations section
    portfolioOperations: [
      {
        company: 'Confer',
        update: 'Partnering with Confer + consumer lending to identify opportunities to increase our funding rates for pre-approved loans. The focus is to remove manual work on pre-approved CCs and personal loans to allow the team to focus on pull-through rates on auto loans. Auto loans are more manual and represent the largest amount of loans that are approved but not funded.'
      },
      {
        company: 'ModernFi',
        update: 'Jake is working with ModernFi to get onboarded, Michael is working on specific tasks for Northern Trust to get us approved for the platform. Targeting a test in January 2026 to take on deposits from the network.'
      },
      {
        company: 'ScribeUp',
        update: 'TK and ScribeUp are working with Alkami to get development work prioritized for widgets and improving our member engagement. Our goal is still to get to 100K members using ScribeUp to increase member engagement. This is a big opportunity to attack active checking accounts in 2026.'
      },
      {
        company: 'Saris AI',
        update: 'Development with the mortgages team is continuing to go well with the target being to begin testing in 2025. Meeting with Rob and Crystal to start identifying success metrics we can track for before/after analysis on operational efficiency.'
      }
    ],

    // Investment Pipeline section
    investmentPipeline: [
      {
        company: 'Silvur',
        status: 'Legal Diligence',
        description: 'Opportunity to bring retirement planning and Medicare to our member experience. These are high-touch items our members are actively asking for help with (per-member surveys). This deal is currently in legal diligence for the investment. The next step is to get a commercial agreement approved by Legal.'
      },
      {
        company: 'FileFlex',
        status: 'Negotiation',
        description: 'Product selected by our security team as a vendor to solve security and member concerns over sharing large files and files with sensitive information over email. Alex Laham and team looked at many different vendors including Box, Mimecast, and Office 365 but found FileFlex\'s proprietary software to have the right mix of security features and usability. This deal is being negotiated to include a mix of discounts and warrants based on business growth.'
      },
      {
        company: 'VisionFi',
        status: 'Testing',
        description: 'Working on testing VisionFi across several groups: Audit, Legal, and Vendor Management to see what VisionFi\'s legal inspection AI models can provide to help scale our back-office teams.'
      }
    ],

    // Customizable topics for this reporting period
    otherTopics: null
  }
];

// Helper function to get the most recent board report
export const getMostRecentReport = () => {
  return boardMeetingReports.sort((a, b) => new Date(b.date) - new Date(a.date))[0];
};

// Helper function to get reports by year
export const getReportsByYear = (year) => {
  return boardMeetingReports.filter(report => report.year === year);
};

// Helper function to get all available years
export const getAvailableYears = () => {
  const years = [...new Set(boardMeetingReports.map(report => report.year))];
  return years.sort((a, b) => b - a);
};

// ================================
// ACTION ITEMS
// ================================
export const actionItems = [
  {
    item: 'Sample Item 1',
    priority: 'high',
    owner: 'Brian & Strategy Committee',
    deadline: 'Jan 15, 2026',
    amount: '$18M',
    description: 'Sample item for the board to approve.'
  }
];

// ================================
// CURQL FUND INVESTMENTS
// ================================
export const curqlFunds = [
  {
    id: 'curql-fund-1',
    name: 'CURQL Fund I',
    commitment: 2500000,
    invested: 1800000,
    assetValue2025: 1800000,
    currentValue: 2614993
  },
  {
    id: 'curql-fund-2',
    name: 'CURQL Fund II',
    commitment: 2500000,
    invested: 300000,
    assetValue2025: 300000,
    currentValue: 300000
  }
];

// ================================
// PORTFOLIO COMPANIES
// ================================
export const portfolioCompanies = [
  {
    id: 'posh',
    name: 'Posh',
    sector: 'AI Tools',
    icon: 'Shield',
    
    // Investment Details
    commitment: 250000,
    invested: 250000,
    assetValue2025: 250000,
    currentValue: 250000,
    investmentDate: '2019',
    equity: 'Equity',
    
    // Status & Integration
    status: 'Active',
    integration: '100%',
    growthRate: '0%',
    adoption: 'Currently Onboarding',
    roi: '1.0x',
    internalROI: 0.641,

    // Product Information
    productOverview: 'AI-powered customer service platform for credit unions, providing voice and chat automation',
    useCases: [
      'AI voice bot to defer call center calls.',
      'AI chat bot for members or prospective members landing on the Service CU website.',
      'Generative AI search functionality on Service CU website.'
    ],
    monthlyHighlight: 'Service CU updated our call center...',
    
    // Financial Performance
    returns: {
      equity: 0,
      efficiency: 111780,
      income: 0,
      contract: 48583,
      total: 160364
    },

    // Monthly Returns Data
    monthlyReturns: [
      { month: 'Jan', efficiency: 6680, income: 0, contract: 4417, cumulative: 11097 },
      { month: 'Feb', efficiency: 6619, income: 0, contract: 4417, cumulative: 22133 },
      { month: 'Mar', efficiency: 9798, income: 0, contract: 4417, cumulative: 36348 },
      { month: 'Apr', efficiency: 5940, income: 0, contract: 4417, cumulative: 46704 },
      { month: 'May', efficiency: 8076, income: 0, contract: 4417, cumulative: 59197 },
      { month: 'Jun', efficiency: 9741, income: 0, contract: 4417, cumulative: 73354 },
      { month: 'Jul', efficiency: 3185, income: 0, contract: 4417, cumulative: 80956 },
      { month: 'Aug', efficiency: 14578, income: 0, contract: 4417, cumulative: 99950 },
      { month: 'Sep', efficiency: 15004, income: 0, contract: 4417, cumulative: 119370 },
      { month: 'Oct', efficiency: 16000, income: 0, contract: 4417, cumulative: 139787 },
      { month: 'Nov', efficiency: 16160, income: 0, contract: 4417, cumulative: 160364 }
    ],
    
    // KPIs
    kpis: [
      { label: 'Calls Contained', value: '16,783', description: 'Calls contained in September 2025', color: 'green' },
      { label: 'Cost Savings', value: '$201,709', description: 'Total cost saved from contained calls', color: 'blue' },
      { label: 'Other Products', value: 'Available', description: 'Opportunity to try AI-powered training platform', color: 'purple' }
    ],
    
    // Metrics
    metrics: [
      {
        name: 'Contract Discount',
        category: 'Contract',
        categoryColor: 'blue',
        definition: 'Commercial agreement (most recent in May 2025) includes a $53,000 annual discount, representing 27% of gross contract value. Brian confirmed with Alamin, SVP Finance, that this is in return for our investment in Posh',
        calculationMethod: 'Calculated contract discount. $53K annual discount, $4,416.67 per month.'
      },
      {
        name: 'Call Deflection',
        category: 'Efficiency',
        categoryColor: 'blue',
        definition: 'Efficiency from call deflection from Posh software. This efficiency is calculated by comparing the average cost per call before Posh was adopted to the current quarter cost per call.',
        calculationMethod: 'Cost of deflecting calls with Posh is calculated by taking the number of calls deflected multiplied by an "average cost per call" for the call center. Currently doing analysis to see which calls are "net new" deflections with Posh compared to our previous vendor.'
      },
      {
        name: 'Chat Deflection',
        category: 'Efficiency',
        categoryColor: 'red',
        definition: 'Efficiency from chat deflection from Posh software. Like call deflection calculations, but focusing on the portion of the call center that answers chats.',
        calculationMethod: 'Cost of deflecting chats from staff is calculated by taking the number of chats deflected multiplied by an "average cost per chat". Generative AI chat is a new feature, so all chats deflected are considered net new value.'
      }
    ],
    
    // Key Milestones
    keyMilestones: [
      { date: '2019', event: 'Initial Investment', amount: '$250K Equity' }
    ]
  },
  
  {
    id: 'confer',
    name: 'Confer Lending',
    sector: 'AI Lending Platform',
    icon: 'Zap',
    
    // Investment Details
    commitment: 500000,
    invested: 500000,
    assetValue2025: 0,
    currentValue: 500000,
    investmentDate: 'May 2025',
    equity: 'LOI',
    
    // Status & Integration
    status: 'Active',
    integration: '100%',
    growthRate: 'Strong',
    adoption: 'Fully Deployed',
    roi: '1.0x',
    internalROI: 2.937,

    // Product Information
    productOverview: 'AI powered lending platform that combines member activity, credit bureau data, and loan stipulations to provide lending pre-approvals to Service CU members.',
    useCases: [
      'Confer drives a substantial amount of organic loan volume at a low cost.',
      'Organic loan volume deepens relationship status with our members.',
      'Expanding to include Deposit products and exploring the opportunity to expand to business accounts.',
      'Confer has signed up 3 new credit unions this quarter and has built a strong pipeline of additional clients.'
    ],
    monthlyHighlight: 'Confer continues to improve their targeting methods and is already outperforming the prior quarter with a week left in the campaign.',
    
    // Financial Performance
    returns: {
      equity: 0,
      efficiency: 1441324,
      income: 27155,
      contract: 0,
      total: 1468479
    },

    // Monthly Returns Data
    monthlyReturns: [
      { month: 'Jan', efficiency: 186130, income: 4046, contract: 0, cumulative: 190176 },
      { month: 'Feb', efficiency: 186130, income: 1626, contract: 0, cumulative: 377931 },
      { month: 'Mar', efficiency: 186130, income: 1425, contract: 0, cumulative: 565486 },
      { month: 'Apr', efficiency: 132670, income: 1541, contract: 0, cumulative: 699697 },
      { month: 'May', efficiency: 132670, income: 2037, contract: 0, cumulative: 834404 },
      { month: 'Jun', efficiency: 132670, income: 2224, contract: 0, cumulative: 969299 },
      { month: 'Jul', efficiency: 78308, income: 2910, contract: 0, cumulative: 1050517 },
      { month: 'Aug', efficiency: 78308, income: 2981, contract: 0, cumulative: 1131807 },
      { month: 'Sep', efficiency: 78308, income: 2402, contract: 0, cumulative: 1212516 },
      { month: 'Oct', efficiency: 125000, income: 2839, contract: 0, cumulative: 1340356 },
      { month: 'Nov', efficiency: 125000, income: 3123, contract: 0, cumulative: 1468479 }
    ],
    
    // KPIs
    kpis: [
      { label: 'Organic Loan Volume', value: '$75M', description: 'Organic loan volume in prior quarter campaign', color: 'green' },
      { label: 'Cost Reduction', value: '95%', description: 'Reduction in Service CU cost to acquire a loan with Confer', color: 'blue' },
      { label: 'Volume Multiple', value: '3X', description: 'Multiple of loan volume with Confer compared to Cunexus', color: 'purple' }
    ],
    
    // Metrics
    metrics: [
      {
        name: 'Acquisition',
        category: 'Efficiency',
        categoryColor: 'blue',
        definition: 'Efficiency driven by Confer low cost to acquire loans. This metric compares Confer to Cunxus and other forms of acquiring loan volume.',
        calculationMethod: 'Confer is compared to Cunexus for "$ funded loans per member targeted". This metric identifies the volume Cunexus would drive for us today (part 1) and the loan growth driven by Confer (part 2). For part 1, we calculate the cost difference in acquiring loans between the two vendors. For part 2, we assume this is loan volume Cunexus could not achieve (on average this is a 42% increase). Efficiency on this additional loan volume is calculated by using indirect lending acquisition rates (1.6%).'
      },
      {
        name: 'Credit card income',
        category: 'Income',
        categoryColor: 'blue',
        definition: 'Interchange income from credit cards funded through Confer software.',
        calculationMethod: 'Isolating credit card transactions for accounts funded through Confer offers. Transactions are evaluated at an average of $0.48 per transaction across credit card products.'
      },
      {
        name: 'Loan Servicing',
        category: 'Efficiency',
        categoryColor: 'red',
        definition: 'Financial savings from our team not having to service the loans from Confer. Using standard business metrics for the time to process a lot and the average cost per hour for employees.',
        calculationMethod: 'Calculating efficiency from auto-funding or changing our internal process for pre-approval loans.'
      },
      {
        name: 'Member Retention',
        category: 'Efficiency',
        categoryColor: 'red',
        definition: 'Calculate efficiency from higher retention rate on members that got a loan from confer compared to the average member.',
        calculationMethod: 'Compare retention rates for Confer users compared to the average Service CU user. These calculations will be done annually on Confer cohorts once they hit 1-year activated.'
      }
    ],
    
    // Key Milestones
    keyMilestones: [
      { date: 'May 2025', event: 'Initial Investment', amount: '$500K LOI' }
    ]
  },
  
  {
    id: 'wealthcabinet',
    name: 'WealthCabinet',
    sector: 'Embedded Wealth',
    icon: 'Users',
    
    // Investment Details
    commitment: 750000,
    invested: 750000,
    assetValue2025: 750000,
    currentValue: 750000,
    investmentDate: 'January 2024',
    equity: 'Equity + Convertible Note + Warrants',
    
    // Status & Integration
    status: 'Active',
    integration: '60%',
    growthRate: '0%',
    adoption: 'Live with Alkami',
    roi: '1.0x',
    internalROI: 0,
    
    // Product Information
    productOverview: 'WealthCabinet is a provider of customized SaaS embedded wealth technology solution and infrastructure for credit unions. This allows Service CU members to trade stocks and cryptocurrencies directly from their Service CU account.',
    useCases: [
      'Investing, Custody, Clearing and Liquidity infrastructure for credit unions',
      'Embedded into Service CU online banking experience',
      'Offers Service CU members trading opportunities to capture younger audience and increase deposit activity.'
    ],
    monthlyHighlight: 'WealthCabinet pilot program is live for employees. Early indicators show a clean, simple UI and the product is working.',

    // Financial Performance
    returns: {
      equity: 0,
      efficiency: 0,
      income: 0,
      contract: 0,
      total: 0
    },

    // Monthly Returns Data
    monthlyReturns: [
      { month: 'Jan', efficiency: 0, income: 0, contract: 0, cumulative: 0 },
      { month: 'Feb', efficiency: 0, income: 0, contract: 0, cumulative: 0 },
      { month: 'Mar', efficiency: 0, income: 0, contract: 0, cumulative: 0 },
      { month: 'Apr', efficiency: 0, income: 0, contract: 0, cumulative: 0 },
      { month: 'May', efficiency: 0, income: 0, contract: 0, cumulative: 0 },
      { month: 'Jun', efficiency: 0, income: 0, contract: 0, cumulative: 0 },
      { month: 'Jul', efficiency: 0, income: 0, contract: 0, cumulative: 0 },
      { month: 'Aug', efficiency: 0, income: 0, contract: 0, cumulative: 0 },
      { month: 'Sep', efficiency: 0, income: 0, contract: 0, cumulative: 0 },
      { month: 'Oct', efficiency: 0, income: 0, contract: 0, cumulative: 0 },
      { month: 'Nov', efficiency: 0, income: 0, contract: 0, cumulative: 0 }
    ],
    
    // KPIs
    kpis: [
      { label: 'Partner Credit Unions', value: '2', description: 'Number of Credit Unions using WealthCabinet today', color: 'green' },
      { label: 'Pilot Status', value: 'In Progress', description: 'Friends and Family pilot program in process', color: 'blue' },
      { label: 'Product Status', value: 'Live', description: 'Early indicators show clean, simple UI and working product', color: 'purple' }
    ],
    
    // Metrics
    metrics: [],
    
    // Key Milestones
    keyMilestones: [
      { date: 'Jan 2024', event: 'Initial Investment', amount: '$250K Equity' },
      { date: 'Apr 2024', event: 'Additional Investment', amount: '$500K Convertible Note' },
      { date: 'Apr 2024', event: 'Award Warrants', amount: '$100K Equity' }
    ]
  },
  
  {
    id: 'scribeup',
    name: 'ScribeUp',
    sector: 'Subscription Management',
    icon: 'DollarSign',
    
    // Investment Details
    commitment: 500000,
    invested: 500000,
    assetValue2025: 500000,
    currentValue: 500000,
    investmentDate: 'May 2025',
    equity: 'SAFE',
    
    // Status & Integration
    status: 'Active',
    integration: '100%',
    growthRate: 'Strong',
    adoption: 'Fully Deployed, 7% Member Adoption',
    roi: '1.0x',
    internalROI: 0.531,

    // Product Information
    productOverview: 'Subscription management platform with AI-powered prediction engine that helps members identify, track, optimize, and proactively manage recurring payments across all financial accounts with predictive cancellation recommendations. ScribeUp helps our members save money and increase Service CU share of member wallet.',
    useCases: [
      'Cancel unwanted subscriptions automatically with one-click termination and negotiation',
      'Track spending patterns across multiple services with AI-powered predictive insights',
      'Scan accounts internal and external to Service CU with the ability to easily transfer subscriptions to Service CU accounts',
      'Receive personalized alerts for price increases with alternative recommendations and savings opportunities'
    ],
    monthlyHighlight: 'ScribeUp has exceeded expectations with 24,500 active users generating $250 average annual savings per member, demonstrating exceptional ROI and member satisfaction at 4.8/5 rating. We expect to see increased activation with deep-linking capabilities in Alkami. That integration will be live when Alkami launches.',
    
    // Financial Performance
    returns: {
      equity: 0,
      efficiency: 106565,
      income: 108716,
      contract: 50434,
      total: 265715
    },

    // Monthly Returns Data
    monthlyReturns: [
      { month: 'Jan', efficiency: 3032, income: 10841, contract: 2083, cumulative: 15956 },
      { month: 'Feb', efficiency: 3167, income: 3532, contract: 1909, cumulative: 24565 },
      { month: 'Mar', efficiency: 4807, income: 11344, contract: 2145, cumulative: 42860 },
      { month: 'Apr', efficiency: 6674, income: 8840, contract: 3031, cumulative: 61406 },
      { month: 'May', efficiency: 8141, income: 14649, contract: 3531, cumulative: 87727 },
      { month: 'Jun', efficiency: 8861, income: 8394, contract: 4003, cumulative: 108985 },
      { month: 'Jul', efficiency: 10676, income: 14744, contract: 4379, cumulative: 138784 },
      { month: 'Aug', efficiency: 13528, income: 18104, contract: 5707, cumulative: 176122 },
      { month: 'Sep', efficiency: 14885, income: 2877, contract: 6720, cumulative: 200604 },
      { month: 'Oct', efficiency: 16235, income: 7391, contract: 8060, cumulative: 232290 },
      { month: 'Nov', efficiency: 16559, income: 8000, contract: 8866, cumulative: 265715 }
    ],
    
    // KPIs
    kpis: [
      { label: 'Active Users', value: '24,500', description: 'Active ScribeUp users', color: 'green' },
      { label: 'Avg. Subscription Savings', value: '$250', description: 'Average annual savings per Service CU member', color: 'blue' },
      { label: 'Member Satisfaction', value: '4.8/5', description: 'Based on 4,200 member reviews and Net Promoter Score', color: 'purple' }
    ],
    
    // Metrics
    metrics: [
      {
        name: 'Contract Discount',
        category: 'Contract',
        categoryColor: 'blue',
        definition: '60% commercial discount on ScribeUp in return for our capital investment.',
        calculationMethod: 'List price is $0.50 per month, we pay %0.20, representing a 60% discount. This is calculated using the monthly billing statements from Accounting.'
      },
      {
        name: 'Debit card income',
        category: 'Income',
        categoryColor: 'blue',
        definition: 'Increase in Debit card activity for ScribeUp users compared to their usage patterns before ScribeUp.',
        calculationMethod: 'Isolate increased debit card activity for ScribeUp users and multiple by our net interchange income per transaction.'
      },
      {
        name: 'Credit card income',
        category: 'Income',
        categoryColor: 'blue',
        definition: 'Increase in Credit card activity for ScribeUp users compared to their usage patterns before ScribeUp.',
        calculationMethod: 'Isolate increased credit card activity for ScribeUp users and multiply transactions by our average net non-interest income.'
      },
      {
        name: 'Member Retention',
        category: 'Efficiency',
        categoryColor: 'red',
        definition: 'Compare retention on ScribeUp users to retention metrics for Service CU and online banking members.',
        calculationMethod: 'Compare retention rates for ScribeUp users compared to the average Service CU user. These calculations will be done annually on ScribeUp cohorts once they hit 1-year activated.'
      }
    ],
    
    // Key Milestones
    keyMilestones: [
      { date: 'May 2025', event: 'Initial Investment', amount: '$500K SAFE' }
    ]
  },
  
  {
    id: 'modernfi',
    name: 'ModernFi',
    sector: 'Deposit Solutions',
    icon: 'Shield',
    
    // Investment Details
    commitment: 250000,
    invested: 250000,
    assetValue2025: 250000,
    currentValue: 250000,
    investmentDate: 'October 2024',
    equity: 'Equity',
    
    // Status & Integration
    status: 'Onboarding',
    integration: '50%',
    growthRate: '0%',
    adoption: 'Currently Onboarding',
    roi: '1.0x',
    internalROI: 0.088,

    // Product Information
    productOverview: 'First of its kind credit union deposit network. ModernFi is helping credit unions grow deposits to drive growth and offer extended insurance to credit union members.',
    useCases: [
      'Acquire deposits during high-demand periods with competitive nationwide rates and automated bidding',
      'Optimize deposit pricing and terms using real-time market intelligence and predictive analytics',
      'Unlock the ability to host municipality deposits and go after large deposit business accounts'
    ],
    monthlyHighlight: 'Service CU has signed a contract with ModernFi to join the deposit network. Phase 1, the ability for Service to take on deposits from the network, is set to go live in October. We are saving an expected $10K per month from ModernFi pricing discount from investment.',
    
    // Financial Performance
    returns: {
      equity: 0,
      efficiency: 0,
      income: 0,
      contract: 22000,
      total: 22000
    },
    
    // Monthly Returns Data
    monthlyReturns: [
      { month: 'Jan', efficiency: 0, income: 0, contract: 0, cumulative: 0 },
      { month: 'Feb', efficiency: 0, income: 0, contract: 0, cumulative: 0 },
      { month: 'Mar', efficiency: 0, income: 0, contract: 0, cumulative: 0 },
      { month: 'Apr', efficiency: 0, income: 0, contract: 0, cumulative: 0 },
      { month: 'May', efficiency: 0, income: 0, contract: 0, cumulative: 0 },
      { month: 'Jun', efficiency: 0, income: 0, contract: 0, cumulative: 0 },
      { month: 'Jul', efficiency: 0, income: 0, contract: 0, cumulative: 0 },
      { month: 'Aug', efficiency: 0, income: 0, contract: 0, cumulative: 0 },
      { month: 'Sep', efficiency: 0, income: 0, contract: 10000, cumulative: 10000 },
      { month: 'Oct', efficiency: 0, income: 0, contract: 6000, cumulative: 16000 },
      { month: 'Nov', efficiency: 0, income: 0, contract: 6000, cumulative: 22000 }
    ],

    // KPIs
    kpis: [
      { label: 'Partner Credit Unions', value: '20', description: 'Active network participants in deposit marketplace', color: 'green' },
      { label: 'Deposits Facilitated', value: '$85M', description: 'Total deposit volume facilitated YTD', color: 'blue' },
      { label: 'Service CU Integration', value: 'Phase 1', description: 'Ability to take on deposits from the CU deposit network expected in October', color: 'purple' }
    ],
    
    // Metrics
    metrics: [
      {
        name: 'Contract Discount',
        category: 'Contract',
        categoryColor: 'blue',
        definition: 'Platform fee of $10K a month is eliminated. Instead, we committed to a usage minimum until we hit the "platform fee" cost. Additionally, there is a 25% reduction on all usage-based fees on the ModernFi platform.',
        calculationMethod: 'Before hitting usage minimum: $10K - commitment - usage. This accounts for the $10K in savings less and minimum fees Service CU still pays. After hitting the minimum, the savings will be $10K per month.'
      },
      {
        name: 'Rate Arbitrage',
        category: 'Income',
        categoryColor: 'red',
        definition: 'Potential for income to be generated from sending deposits into the ModernFi network. Also the possibility that ModernFi network cost is lower than purchasing other insurance products.',
        calculationMethod: 'Calculation to be determined based on market and network conditions.'
      },
      {
        name: 'Cost to Acquire Funds',
        category: 'Efficiency',
        categoryColor: 'red',
        definition: 'Potential benefit for a low cost to acquire deposits.',
        calculationMethod: 'Calculation to be determined based on market and network conditions.'
      }
    ],
    
    // Key Milestones
    keyMilestones: [
      { date: 'October 2024', event: 'Initial Investment', amount: '$250K Equity' }
    ]
  },
  
  {
    id: 'larky',
    name: 'Larky',
    sector: 'Mobile Engagement',
    icon: 'Target',
    
    // Investment Details
    commitment: 250000,
    invested: 250000,
    assetValue2025: 250000,
    currentValue: 250000,
    investmentDate: 'November 2024',
    equity: 'Convertible Note',
    
    // Status & Integration
    status: 'Not Active',
    integration: '0%',
    growthRate: 'n.a.',
    adoption: 'Blocked by Alkami Migration',
    roi: '1.0x',
    internalROI: 0,
    
    // Product Information
    productOverview: 'Larky utilizes mobile push notifications to get in touch with members when and where they need you most.',
    useCases: [
      'Personalized member communications',
      'Targeted product offers',
      'Engagement tracking',
      'Mobile app enhancement'
    ],
    monthlyHighlight: 'Larky finished their Alkami integration in September 2025. They continue to grow their channel partners and have success with a channel sales motion. We will discuss using Larky once we get through the first wave of Alkami onboarding.',

    // Financial Performance
    returns: {
      equity: 0,
      efficiency: 0,
      income: 0,
      contract: 0,
      total: 0
    },

    // Monthly Returns Data
    monthlyReturns: [
      { month: 'Jan', efficiency: 0, income: 0, contract: 0, cumulative: 0 },
      { month: 'Feb', efficiency: 0, income: 0, contract: 0, cumulative: 0 },
      { month: 'Mar', efficiency: 0, income: 0, contract: 0, cumulative: 0 },
      { month: 'Apr', efficiency: 0, income: 0, contract: 0, cumulative: 0 },
      { month: 'May', efficiency: 0, income: 0, contract: 0, cumulative: 0 },
      { month: 'Jun', efficiency: 0, income: 0, contract: 0, cumulative: 0 },
      { month: 'Jul', efficiency: 0, income: 0, contract: 0, cumulative: 0 },
      { month: 'Aug', efficiency: 0, income: 0, contract: 0, cumulative: 0 },
      { month: 'Sep', efficiency: 0, income: 0, contract: 0, cumulative: 0 },
      { month: 'Oct', efficiency: 0, income: 0, contract: 0, cumulative: 0 },
      { month: 'Nov', efficiency: 0, income: 0, contract: 0, cumulative: 0 }
    ],
    
    // KPIs
    kpis: [
      { label: 'Service CU Members', value: '0', description: 'Service CU is not a customer of Larky', color: 'orange' },
      { label: 'CU Customers', value: '285', description: 'Number of credit union customers as of last quarter', color: 'blue' },
      { label: 'Average ARR per CU', value: '$30K', description: 'Average ARR per credit union customer', color: 'purple' }
    ],
    
    // Metrics
    metrics: [
      {
        name: 'Contract Discount',
        category: 'Contract',
        categoryColor: 'red',
        definition: '33% reduction in expense when Service CU joins the platform.',
        calculationMethod: 'No calculation method specified'
      }
    ],
    
    // Key Milestones
    keyMilestones: [
      { date: 'November 2024', event: 'Initial Investment', amount: '$250K Convertible Note' }
    ]
  },
  
  {
    id: 'cuautofi',
    name: 'CUautoFi',
    sector: 'Indirect Lending',
    icon: 'TrendingUp',
    
    // Investment Details
    commitment: 500000,
    invested: 500000,
    assetValue2025: 500000,
    currentValue: 500000,
    investmentDate: 'February 2025',
    equity: '50% Partner',
    
    // Status & Integration
    status: 'Onboarding',
    integration: '50%',
    growthRate: 'n.a.',
    adoption: 'Currently Onboarding',
    roi: '1.0x',
    internalROI: 0.337,

    // Product Information
    productOverview: 'Indirect lending company spun out of Langley FCU that we became a 50% partner of. The goal is to de-couple indirect and direct auto lending at Service CU and drive additional non-interest income for the credit union by selling loan volume to other participating institutions.',
    useCases: [
      'Indirect auto lending network',
      'Dealer relationship management',
      'Loan volume generation',
      'Non-interest income creation'
    ],
    monthlyHighlight: 'Onboarding and testing still in process. The team is figuring out what the best go live date is to start targeting dealers to switch networks.',
    
    // Financial Performance
    returns: {
      equity: 0,
      efficiency: 168637,
      income: 0,
      contract: 0,
      total: 168637
    },
    
    // Monthly Returns Data
    monthlyReturns: [
      { month: 'Jan', efficiency: 0, income: 0, contract: 0, cumulative: 0 },
      { month: 'Feb', efficiency: 0, income: 0, contract: 0, cumulative: 0 },
      { month: 'Mar', efficiency: 0, income: 0, contract: 0, cumulative: 0 },
      { month: 'Apr', efficiency: 0, income: 0, contract: 0, cumulative: 0 },
      { month: 'May', efficiency: 0, income: 0, contract: 0, cumulative: 0 },
      { month: 'Jun', efficiency: 3697, income: 0, contract: 0, cumulative: 3697 },
      { month: 'Jul', efficiency: 19464, income: 0, contract: 0, cumulative: 23161 },
      { month: 'Aug', efficiency: 36369, income: 0, contract: 0, cumulative: 59530 },
      { month: 'Sep', efficiency: 36369, income: 0, contract: 0, cumulative: 95899 },
      { month: 'Oct', efficiency: 36369, income: 0, contract: 0, cumulative: 132268 },
      { month: 'Nov', efficiency: 36369, income: 0, contract: 0, cumulative: 168637 }
    ],

    // KPIs
    kpis: [
      { label: 'Active Dealers', value: '0', description: 'Active dealers from Service CU network converted to CUautoFi', color: 'orange' },
      { label: 'Total Dealer Network', value: '600', description: 'Total CUautoFi dealer network', color: 'blue' },
      { label: 'Monthly Net Income', value: '$0', description: 'Average monthly net income', color: 'purple' }
    ],
    
    // Metrics
    metrics: [
      {
        name: 'Employee Cross-Charge',
        category: 'Efficiency',
        categoryColor: 'red',
        definition: 'Cross-charge for indirect lending employees doing work for CUautoFi. Costs include: Salary, benefits, and bonus accrual for Service Credit Union bonus plan.',
        calculationMethod: 'Set dollar amount for the total employee cost of all Service CU employees leased to CUautoFi. Currently calculated at $54,526 per month, amount updated by HR on a quarterly basis. If an employee leaves Service CU, we will remove their income from this calculation.'
      },
      {
        name: 'Shared Services Agreement',
        category: 'Efficiency',
        categoryColor: 'red',
        definition: 'Shared costs that Service CU pays for including: IT, Legal, Accounting, and leased office space.',
        calculationMethod: 'Set dollar amount, agreed upon by CUautoFi board of directors. Estimate this to be a $10K per month cross-charge.'
      },
      {
        name: 'Indirect Downsizing',
        category: 'Efficiency',
        categoryColor: 'blue',
        definition: 'Due to the CUautoFi merger, we exited multiple employees from Service CU.',
        calculationMethod: 'Total employee cost for Service CU employees terminated as part of the CUautoFi deal, net of all severance costs. Total cost $36,369 per month.'
      },
      {
        name: 'Distributions',
        category: 'Income',
        categoryColor: 'red',
        definition: 'Distributions for Class A owners. Paid Quarterly. Service CU is paid distributions on our loans purchased and 50% of loans purchased by anyone other than Langley.',
        calculationMethod: 'Class A ownership distributions are on a quarterly basis. This represents: patronage distributions for Class A owners, 50% of all other profits.'
      }
    ],
    
    // Key Milestones
    keyMilestones: [
      { date: 'February 2025', event: 'Initial Investment', amount: '$500K Equity' }
    ]
  },
  
  {
    id: 'saris',
    name: 'Saris AI',
    sector: 'Agentic AI',
    icon: 'CheckCircle',
    
    // Investment Details
    commitment: 500000,
    invested: 500000,
    assetValue2025: 0,
    currentValue: 500000,
    investmentDate: 'May 2025',
    equity: 'LOI',
    
    // Status & Integration
    status: 'Onboarding',
    integration: '30%',
    growthRate: 'n.a.',
    adoption: 'Currently Onboarding',
    roi: '1.0x',
    internalROI: 0.275,

    // Product Information
    productOverview: 'Agentic AI company working to automate end-to-end back office operations for credit unions and community banks.',
    useCases: [
      'End-to-end automation of mortgages process. Opportunity to increase department capacity by 5X.',
      'Automation of inheritor services process to reduce the time to service a case by 80%.',
      'Future opportunity to improve efficiency and bottom line income for CUautoFi and other Service CU processes.'
    ],
    monthlyHighlight: 'Saris AI is making progress on the mortgage department automation. The team is working through the technical requirements and workflow mapping to ensure smooth deployment of the agentic AI systems.',
    
    // Financial Performance
    returns: {
      equity: 0,
      efficiency: 0,
      income: 0,
      contract: 137500,
      total: 137500
    },
    
    // Monthly Returns Data
    monthlyReturns: [
      { month: 'Jan', efficiency: 0, income: 0, contract: 0, cumulative: 0 },
      { month: 'Feb', efficiency: 0, income: 0, contract: 0, cumulative: 0 },
      { month: 'Mar', efficiency: 0, income: 0, contract: 0, cumulative: 0 },
      { month: 'Apr', efficiency: 0, income: 0, contract: 0, cumulative: 0 },
      { month: 'May', efficiency: 0, income: 0, contract: 0, cumulative: 0 },
      { month: 'Jun', efficiency: 0, income: 0, contract: 22917, cumulative: 22917 },
      { month: 'Jul', efficiency: 0, income: 0, contract: 22917, cumulative: 45834 },
      { month: 'Aug', efficiency: 0, income: 0, contract: 22917, cumulative: 68751 },
      { month: 'Sep', efficiency: 0, income: 0, contract: 22917, cumulative: 91668 },
      { month: 'Oct', efficiency: 0, income: 0, contract: 22917, cumulative: 114583 },
      { month: 'Nov', efficiency: 0, income: 0, contract: 22917, cumulative: 137500 }
    ],

    // KPIs
    kpis: [
      { label: 'Department Focus', value: 'Mortgages', description: 'First department onboarding to Saris AI', color: 'green' },
      { label: 'Contractual Discount', value: '50%', description: 'Contractual discount in return for investment', color: 'blue' },
      { label: 'Capacity Increase', value: '5X', description: 'Potential department capacity increase through automation', color: 'purple' }
    ],
    
    // Metrics
    metrics: [
      {
        name: 'Contract Discount',
        category: 'Contract',
        categoryColor: 'blue',
        definition: '50% contractual discount on all current and future Saris AI products.'
      }
    ],
    
    // Key Milestones
    keyMilestones: [
      { date: 'May 2025', event: 'Initial Investment', amount: '$500K LOI' }
    ]
  }
];

// ================================
// PIPELINE COMPANIES
// ================================
export const pipelineCompanies = [
  {
    id: 'silvur',
    name: 'Silvur',
    sector: 'Retirement & Medicare Solutions',
    stage: 'Series A',
    investmentAmount: '$500K',
    timeline: 'Q4 2025',
    priority: 'high',
    status: 'active',
    description: 'Medicare and retirement planning platform that would help round out our product offering for the NH audience preparing for or actively navigating retirement.|Investment would significantly reduce expenses: eliminate $200K Medicare onboarding fee and 30% reduction in ongoing platform cost. In year 1, we would recoup 50% of our total capital investment.|This business would create a new stream of non-interest income around Medicare premiums. Conservative models put the net non-interest income to Service CU at $150K per year in the next three years.',
    investmentMetrics: [
      {
        metric: 'Medicare Onboarding Fee Elimination',
        category: 'Efficiency',
        calculation: 'Eliminate $200K one-time onboarding fee',
        benefit: 'Immediate cost avoidance in Year 1'
      },
      {
        metric: 'Platform Cost Reduction',
        category: 'Contract',
        calculation: '30% reduction in ongoing platform costs',
        benefit: 'Recurring annual savings on Medicare platform'
      },
      {
        metric: 'Medicare Premium Income',
        category: 'Income',
        calculation: '$150K net non-interest income per year',
        benefit: 'New revenue stream from Medicare premium referrals'
      },
      {
        metric: 'Capital Recoup',
        category: 'Efficiency',
        calculation: '50% of investment recouped in Year 1',
        benefit: 'Fast payback period on capital investment'
      }
    ],
    keyMetrics: {
      targetMarket: '60+ year old NH residents thinking about financial aspects of retirement.',
      currentRevenue: 'unknown',
      growth: 'unknown'
    },
    serviceCUFit: 'High - Retirement is high on the list of items that our members asked for help with. NH is an aging state, large opportunity to be a source of truth for this population.',
    nextSteps: 'We have approval to move forward with the commercial agreement, pending approval on th asset investment. This investment will substantially reduce operating expenses in 2026. Brian is working with Jill to get a preliminary review of the investment documents.'
  },
  {
    id: 'qnext',
    name: 'QNext (FileFlex)',
    sector: 'Security',
    stage: 'Seed',
    investmentAmount: '$250K-$500K',
    timeline: 'Q4 2025',
    priority: 'high',
    status: 'active',
    description: 'QNext provides zero-trust data access. Service CU is already using FileFlex for some back office operations (Audit, Compliance) as a test use case, interested in expanding to member communications next year. The product enables us to easily and securely communicate files to members or other external services (Audit, NCUA).|I have access to the company data room and am in the process of making an investment decision. I think there is a lot of value in this product, but the business does not currently have good measures for quantifying the value of security risk mitigation.',
    investmentMetrics: [
      {
        metric: 'Secure Document Sharing',
        category: 'Efficiency',
        calculation: 'Reduce time spent on secure file transfers by internal teams',
        benefit: 'Operational efficiency for Audit, Compliance, and member communications'
      },
      {
        metric: 'Security Risk Mitigation',
        category: 'Efficiency',
        calculation: 'Zero-trust data access reduces breach exposure',
        benefit: 'Reduced regulatory and reputational risk from data incidents'
      },
      {
        metric: 'Member Experience',
        category: 'Efficiency',
        calculation: 'Solve member concerns around secure document sharing',
        benefit: 'Improved NPS and member satisfaction with document exchange'
      },
      {
        metric: 'Contract Discount',
        category: 'Contract',
        calculation: 'Negotiated discount schedule based on investment',
        benefit: 'Reduced platform costs compared to list pricing'
      }
    ],
    keyMetrics: {
      targetMarket: 'unknown',
      currentRevenue: 'unknown',
      growth: 'unknown'
    },
    serviceCUFit: 'High - this is a product our team already identified as the market winner. This also provides significant security value, helps solve concerns members have surfaced around secure document sharing, and will provide operational efficiency for internal teams.',
    nextSteps: 'Currently working on terms of the investment with Alex Laham and the FileFlex business owners. This includes: discount schedules, incentive warrants, and working on a shared marketing plan.'
  },
  {
    id: 'visionfi',
    name: 'VisionFi',
    sector: 'AI',
    stage: 'Seed',
    investmentAmount: '$250K-$500K',
    timeline: 'Q1 2026',
    priority: 'medium',
    status: 'active',
    description: 'VisionFi is an AI company that builds and maintains models that perform deep legal inspection of financial documents to help banks and credit unions streamline back office operations in lending, fraud, audit, legal, and other key areas.|We have been working with Ronny (founder and CEO) for a few months identifying the best way to test the product. As a team we are looking to test: vendor contracts, vendor due diligence, and inspecting loans (starting with indirect lending).|The product is going to be tested by sharing documents via FileFlex for security.',
    investmentMetrics: [
      {
        metric: 'Back Office Automation',
        category: 'Efficiency',
        calculation: 'Reduce time spent on document review in lending, fraud, audit, legal',
        benefit: 'Scale back office operations without proportional headcount increase'
      },
      {
        metric: 'Vendor Due Diligence',
        category: 'Efficiency',
        calculation: 'Streamline vendor contract review and due diligence process',
        benefit: 'Faster vendor onboarding with consistent compliance review'
      },
      {
        metric: 'Loan Inspection',
        category: 'Efficiency',
        calculation: 'AI-powered inspection of indirect lending documents',
        benefit: 'Improved loan quality and reduced compliance risk'
      },
      {
        metric: 'RPA Complement',
        category: 'Efficiency',
        calculation: 'Address processes below Saris AI ROI threshold',
        benefit: 'Expand automation coverage to more back office functions'
      }
    ],
    keyMetrics: {
        targetMarket: 'unknown',
        currentRevenue: 'unknown',
        growth: 'unknown'
    },
    serviceCUFit: 'High - there are a lot of areas in our operations that could benefit from this type of model. This type of partner would work perfectly with our RPA initiative to help reach the processes that do not have a high enough ROI to warrant a Saris AI type engagement.',
    nextSteps: 'Working with business owners (Alex Laham, Jill Cote, Crystal Richardson) to gather documents and analyze model results.'
  }
];

// ================================
// ANALYSIS DATA
// ================================
export const analysisItems = [
  {
    id: 'confer-lending-analysis',
    title: 'Confer Lending Volume Analysis',
    companyId: 'confer',
    category: 'Portfolio Analysis',
    date: 'October 2025',
    status: 'working',
    executiveSummary: 'Analyzing Confer Lending compared to Cunexus to determine the value Confer is driving for Service CU loan growth and efficiency. This analysis examines loan volume trends, processing efficiency improvements, and the impact on member experience to quantify the strategic value of our Confer investment.'
  },
  {
    id: 'direct-deposit-analysis',
    title: 'Direct Deposit Analysis',
    companyId: null,
    category: 'Member Analytics',
    date: 'October 2025',
    status: 'working',
    executiveSummary: 'Analysis of Service CU patterns on direct deposit, specifically looking at geography (USA vs DEU), member age, and average direct deposit amounts. This analysis will help identify opportunities to increase direct deposit adoption and understand member demographics to better target financial products and services.'
  },
  {
    id: 'scribe-up-analysis',
    title: 'ScribeUp Deep Dive Analysis',
    companyId: 'scribeup',
    category: 'Portfolio Analysis',
    date: 'October 2025',
    status: 'completed',
    executiveSummary: 'Comprehensive review of ScribeUp performance, member adoption, and strategic value to Service CU operations. The analysis shows strong member engagement with 20,157 active members, demonstrating significant value in credit score monitoring and financial wellness. ScribeUp continues to drive member engagement and provides a competitive advantage in helping members improve their financial health.'
  }
];

// ================================
// HELPER FUNCTIONS
// ================================

// Portfolio Company Helpers
export const getCompanyById = (id) => {
  return portfolioCompanies.find(company => company.id === id);
};

export const getCompaniesByStatus = (status) => {
  return portfolioCompanies.filter(company => company.status === status);
};

export const getActiveCompanies = () => {
  return getCompaniesByStatus('Active');
};

export const getAllCompanies = () => {
  return portfolioCompanies;
};

// Pipeline Helpers
export const getPipelineCompanies = () => {
  return pipelineCompanies;
};

export const getPipelineByPriority = (priority) => {
  return pipelineCompanies.filter(company => company.priority === priority);
};

export const getPipelineSummary = () => {
  return {
    totalCompanies: pipelineCompanies.length,
    totalPipeline: pipelineCompanies.reduce((sum, company) => {
      const amount = parseInt(company.investmentAmount.replace(/[$K,-]/g, '')) * 1000;
      return sum + amount;
    }, 0),
    priorityBreakdown: {
      high: pipelineCompanies.filter(c => c.priority === 'high').length,
      medium: pipelineCompanies.filter(c => c.priority === 'medium').length,
      low: pipelineCompanies.filter(c => c.priority === 'low').length
    },
    targetTimeline: 'Q4 2025'
  };
};

// CURQL Helpers
export const getCurqlTotals = () => {
  return curqlFunds.reduce((totals, fund) => ({
    commitment: totals.commitment + fund.commitment,
    invested: totals.invested + fund.invested,
    assetValue2025: totals.assetValue2025 + fund.assetValue2025,
    currentValue: totals.currentValue + fund.currentValue
  }), {
    commitment: 0,
    invested: 0,
    assetValue2025: 0,
    currentValue: 0
  });
};

// Portfolio Aggregation Helpers
export const calculatePortfolioTotals = () => {
  return portfolioCompanies.reduce((totals, company) => ({
    committed: totals.committed + company.commitment,
    invested: totals.invested + company.invested,
    currentValue: totals.currentValue + company.currentValue,
    equity: totals.equity + (company.returns.equity || 0),
    efficiency: totals.efficiency + company.returns.efficiency,
    income: totals.income + company.returns.income,
    contract: totals.contract + company.returns.contract,
    total: totals.total + company.returns.total
  }), {
    committed: 0,
    invested: 0,
    currentValue: 0,
    equity: 0,
    efficiency: 0,
    income: 0,
    contract: 0,
    total: 0
  });
};

export const calculateMonthlyAggregates = () => {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep'];
  
  return months.map(month => {
    const monthData = portfolioCompanies.reduce((acc, company) => {
      const companyMonth = company.monthlyReturns?.find(m => m.month === month);
      if (companyMonth) {
        return {
          efficiency: acc.efficiency + companyMonth.efficiency,
          income: acc.income + companyMonth.income,
          contract: acc.contract + companyMonth.contract
        };
      }
      return acc;
    }, { efficiency: 0, income: 0, contract: 0 });
    
    return {
      month,
      ...monthData,
      total: monthData.efficiency + monthData.income + monthData.contract
    };
  });
};

// Analysis Helpers
export const getAnalysisById = (id) => {
  return analysisItems.find(analysis => analysis.id === id);
};

export const getAnalysisByCategory = (category) => {
  return analysisItems.filter(analysis => analysis.category === category);
};

export const getAnalysisCategories = () => {
  return [...new Set(analysisItems.map(analysis => analysis.category))];
};

// Portfolio Metrics for Dashboard
export const getPortfolioMetrics = () => {
  const totals = calculatePortfolioTotals();
  const activeCompanies = portfolioCompanies.filter(c => c.status === 'Active').length;
  
  return {
    totalCompanies: portfolioCompanies.length,
    activeCompanies: activeCompanies,
    deploymentRate: totals.invested / 5000000, // Assuming $5M fund
    totalReturns: totals.total,
    efficiency: totals.efficiency,
    income: totals.income,
    contract: totals.contract
  };
};

// ================================
// BACKWARD COMPATIBILITY EXPORTS
// ================================

// For components that expect companiesData object (keyed by ID)
export const companiesData = portfolioCompanies.reduce((acc, company) => {
  acc[company.id] = company;
  return acc;
}, {});

// For components that expect companiesList array
export const companiesList = portfolioCompanies;

// For components that expect portfolioData
export const portfolioData = companiesData;

// Calculate and export portfolio summary
const totals = calculatePortfolioTotals();
const activeCompanies = portfolioCompanies.filter(c => c.status === 'Active');

export const portfolioSummary = {
  totalCompanies: portfolioCompanies.length,
  activeCompanies: activeCompanies.length,
  integrationRate: `${Math.round((activeCompanies.length / portfolioCompanies.length) * 100)}%`,
  totalDeployed: `$${(totals.invested / 1000000).toFixed(1)}M`,
  ytdEfficiency: `$${Math.round(totals.efficiency / 1000)}K`,
  ytdIncome: `$${Math.round(totals.income / 1000)}K`
};

// Format pipeline data for components
const pipelineSummary = getPipelineSummary();

export const pipelineData = {
  totalPipeline: `$${Math.round(pipelineSummary.totalPipeline / 1000)}K`,
  totalCompanies: pipelineSummary.totalCompanies,
  priorityBreakdown: pipelineSummary.priorityBreakdown,
  targetTimeline: pipelineSummary.targetTimeline,
  companies: pipelineCompanies
};

// Investment Data Array (for backward compatibility)
export const investmentData = portfolioCompanies;

// Enhanced Company Check (for backward compatibility)
export const isEnhancedCompany = (companyId) => {
  const company = getCompanyById(companyId);
  return company?.monthlyReturns ? true : false;
};



// ================================
// BACKWARD COMPATIBILITY EXPORTS
// (MUST BE AT THE END AFTER EVERYTHING IS DEFINED)
// ================================

// 1. Export colors for backward compatibility
export const colors = config.colors;

// 2. Export validPasswords separately for useAuth.js
export const validPasswords = config.validPasswords;

// 3. Company data getter for PortfolioCompany.jsx
export const getCompanyData = (companyId) => {
  return portfolioCompanies.find(company => company.id === companyId) || companiesData[companyId];
};

// 4. Aggregated totals alias for Dashboard.jsx and Financial.jsx
export const calculateAggregatedTotals = calculatePortfolioTotals;

// 5. CURQL totals alias for Financial.jsx
export const calculateCurqlTotals = getCurqlTotals;

// 6. Total investments calculator for Financial.jsx
export const calculateTotalInvestments = () => {
  return {
    id: 'curql-fund-2',
    name: 'Curql Fund II',
    commitment: 250000,
    invested: 250000,
    assetValue2025: 250000,
    currentValue: 250000
  };
};
